import { useEvent } from "@tamagui/use-event";
import NextHead from "next/head";
import * as React from "react";
import { memo, useEffect, useMemo, useState } from "react";
import { MEDIA, colorSchemes } from "./constants";
import { getSystemTheme, getTheme } from "./helpers";
import { ThemeSettingContext } from "./ThemeSettingContext";
import { useIsomorphicLayoutEffect } from "./useIsomorphicLayoutEffect";
import { Fragment, jsx, jsxs } from "react/jsx-runtime";
const NextThemeProvider = memo(
  ({
    forcedTheme,
    disableTransitionOnChange = !1,
    enableSystem = !0,
    enableColorScheme = !0,
    storageKey = "theme",
    themes = colorSchemes,
    defaultTheme = enableSystem ? "system" : "light",
    attribute = "class",
    skipNextHead,
    onChangeTheme,
    value = {
      dark: "t_dark",
      light: "t_light"
    },
    children
  }) => {
    const [theme, setThemeState] = useState(() => getTheme(storageKey, defaultTheme)), [resolvedTheme, setResolvedTheme] = useState(() => getTheme(storageKey)), attrs = value ? Object.values(value) : themes, handleMediaQuery = useEvent((e) => {
      const _ = getSystemTheme(e), update = () => setResolvedTheme(_);
      disableTransitionOnChange ? update() : React.startTransition(() => update()), theme === "system" && !forcedTheme && handleChangeTheme(_, !1);
    }), handleChangeTheme = useEvent(
      (theme2, updateStorage = !0, updateDOM = !0) => {
        let name = value?.[theme2] || theme2;
        if (updateStorage)
          try {
            localStorage.setItem(storageKey, theme2);
          } catch {
          }
        if (theme2 === "system" && enableSystem) {
          const resolved = getSystemTheme();
          name = value?.[resolved] || resolved;
        }
        if (onChangeTheme?.(name.replace("t_", "")), updateDOM) {
          const d = document.documentElement;
          attribute === "class" ? (d.classList.remove(...attrs), d.classList.add(name)) : d.setAttribute(attribute, name);
        }
      }
    );
    useIsomorphicLayoutEffect(() => {
      const media = window.matchMedia(MEDIA);
      return media.addListener(handleMediaQuery), handleMediaQuery(media), () => {
        media.removeListener(handleMediaQuery);
      };
    }, []);
    const set = useEvent((newTheme) => {
      forcedTheme ? handleChangeTheme(newTheme, !0, !1) : handleChangeTheme(newTheme), setThemeState(newTheme);
    });
    useEffect(() => {
      const handleStorage = (e) => {
        if (e.key !== storageKey)
          return;
        const theme2 = e.newValue || defaultTheme;
        set(theme2);
      };
      return window.addEventListener("storage", handleStorage), () => {
        window.removeEventListener("storage", handleStorage);
      };
    }, [defaultTheme, set, storageKey]), useIsomorphicLayoutEffect(() => {
      if (!enableColorScheme)
        return;
      const colorScheme = (
        // If theme is forced to light or dark, use that
        forcedTheme && colorSchemes.includes(forcedTheme) ? forcedTheme : (
          // If regular theme is light or dark
          theme && colorSchemes.includes(theme) ? theme : (
            // If theme is system, use the resolved version
            theme === "system" && resolvedTheme || null
          )
        )
      );
      (typeof window < "u" && window.matchMedia && window.matchMedia("(prefers-color-scheme: dark)").matches ? "dark" : "light") !== (colorScheme || "light") && document.documentElement.style.setProperty("color-scheme", colorScheme);
    }, [enableColorScheme, theme, resolvedTheme, forcedTheme]);
    const toggle = useEvent(() => {
      const order = resolvedTheme === "dark" ? ["system", "light", "dark"] : ["system", "dark", "light"], next = order[(order.indexOf(theme) + 1) % order.length];
      set(next);
    }), systemTheme = enableSystem ? resolvedTheme : void 0, contextValue = useMemo(() => ({
      theme,
      current: theme,
      set,
      toggle,
      forcedTheme,
      resolvedTheme: theme === "system" ? resolvedTheme : theme,
      themes: enableSystem ? [...themes, "system"] : themes,
      systemTheme
    }), [
      theme,
      set,
      toggle,
      forcedTheme,
      resolvedTheme,
      enableSystem,
      themes,
      systemTheme
    ]);
    return /* @__PURE__ */ jsxs(ThemeSettingContext.Provider, { value: contextValue, children: [
      /* @__PURE__ */ jsx(
        ThemeScript,
        {
          forcedTheme,
          storageKey,
          systemTheme: resolvedTheme,
          attribute,
          value,
          enableSystem,
          defaultTheme,
          attrs,
          skipNextHead
        }
      ),
      useMemo(() => children, [children])
    ] });
  }
), ThemeScript = memo(
  ({
    forcedTheme,
    storageKey,
    attribute,
    enableSystem,
    defaultTheme,
    value,
    attrs,
    skipNextHead
  }) => {
    const optimization = (() => attribute === "class" ? `var d=document.documentElement.classList;${attrs.map((t) => `d.remove('${t}')`).join(";")};` : "var d=document.documentElement;")(), updateDOM = (name, literal) => {
      name = value?.[name] || name;
      const val = literal ? name : `'${name}'`;
      return attribute === "class" ? `d.add(${val})` : `d.setAttribute('${attribute}', ${val})`;
    }, defaultSystem = defaultTheme === "system", contents = /* @__PURE__ */ jsx(Fragment, { children: forcedTheme ? /* @__PURE__ */ jsx(
      "script",
      {
        dangerouslySetInnerHTML: {
          // These are minified via Terser and then updated by hand, don't recommend
          __html: `!function(){${optimization}${updateDOM(forcedTheme)}}()`
        }
      },
      "next-themes-script"
    ) : enableSystem ? /* @__PURE__ */ jsx(
      "script",
      {
        dangerouslySetInnerHTML: {
          __html: `!function(){try {${optimization}var e=localStorage.getItem('${storageKey}');${defaultSystem ? "" : updateDOM(defaultTheme) + ";"}if("system"===e||(!e&&${defaultSystem})){var t="${MEDIA}",m=window.matchMedia(t);m.media!==t||m.matches?${updateDOM(
            "dark"
          )}:${updateDOM("light")}}else if(e) ${value ? `var x=${JSON.stringify(value)};` : ""}${updateDOM(value ? "x[e]" : "e", !0)}}catch(e){}}()`
        }
      },
      "next-themes-script"
    ) : /* @__PURE__ */ jsx(
      "script",
      {
        dangerouslySetInnerHTML: {
          __html: `!function(){try{${optimization}var e=localStorage.getItem("${storageKey}");if(e){${value ? `var x=${JSON.stringify(value)};` : ""}${updateDOM(value ? "x[e]" : "e", !0)}}else{${updateDOM(
            defaultTheme
          )};}}catch(t){}}();`
        }
      },
      "next-themes-script"
    ) });
    return skipNextHead ? contents : /* @__PURE__ */ jsx(NextHead, { children: contents });
  },
  (prevProps, nextProps) => prevProps.forcedTheme === nextProps.forcedTheme
);
export {
  NextThemeProvider
};
//# sourceMappingURL=NextThemeProvider.js.map
